import React, { useEffect, useMemo, useState } from "react";

import { Box } from "@material-ui/core";
import { FindResponse } from "@remar/shared/dist/api/baseApiService";
import { ActionMenu } from "@remar/shared/dist/components/ActionMenu";
import Button from "@remar/shared/dist/components/Button";
import { IColumn } from "@remar/shared/dist/components/MaterialTable";
import { ColumnHeader, StyledCellText, StyledCellWrapper } from "@remar/shared/dist/components/Table/styles";
import { EmptyState } from "@remar/shared/dist/layouts";
import WithTableContentLayout from "@remar/shared/dist/layouts/TableContentLayout";

import DeleteModal from "@remar/shared/dist/modals/DeleteModal";
import { ContentAccessTypes, LiveStream } from "@remar/shared/dist/models";
import { convertDuration } from "@remar/shared/dist/utils/convertMinsToHrsMins";
import { formatDate, formatTime } from "@remar/shared/dist/utils/myAccountUtils";
import { useHistory } from "react-router-dom";
import {
	deleteLiveStream,
	fetchLiveStreamHistory,
	getLiveStreamFullState,
	updateLiveStream
} from "store/features/LiveStream/LiveStream.slice";

import { useAppDispatch, useAppSelector } from "store/hooks";

import CheckPermissions from "core/CheckPermissions";
import { routes } from "core/constants";

const HISTORY_PER_PAGE = 10;
const StreamHistory = () => {
	const history = useHistory();
	const dispatch = useAppDispatch();

	const [deleteStream, setDeleteStream] = useState<LiveStream | null>(null);

	const { isFetchingHistory, streamHistory } = useAppSelector(getLiveStreamFullState);

	useEffect(() => {
		dispatch(
			fetchLiveStreamHistory({
				page: 1,
				perPage: HISTORY_PER_PAGE,
				include: ["allowedLocations"],
				orderBy: { createdAt: "DESC" }
			})
		);
	}, [dispatch]);

	const { page, perPage, items, totalItems } = useMemo(() => {
		if (!streamHistory) {
			return { items: [] } as unknown as FindResponse<LiveStream>;
		}
		const { page, perPage, items, totalItems } = streamHistory;
		return { page, perPage, items, totalItems };
	}, [streamHistory]);

	const getAudience = (type: number, allLocations: boolean, allowedLocations) => {
		let audience = "";

		switch (type) {
			case ContentAccessTypes.Mixed: {
				audience = "Paid, Trial";
				break;
			}
			case ContentAccessTypes.Paid: {
				audience = "Paid";
				break;
			}
			case ContentAccessTypes.Trial: {
				audience = "Trial";
				break;
			}
		}

		if (allowedLocations.length > 0) {
			audience += `, Location (${allowedLocations.length})`;
		} else if (allLocations) {
			audience += ", All Locations";
		}
		return audience;
	};

	const navigateToStream = (id, sId: string) => {
		history.push(`${routes.stream.join.getPath()}/${id}/${sId}`);
	};

	const tableColumns: Array<IColumn<LiveStream>> = useMemo(
		() => [
			{
				alignment: "left",
				label: <ColumnHeader>Name & Description</ColumnHeader>,
				Cell: ({ rowData: { id, name, description, streamId, mainImageUrl } }) => (
					<StyledCellWrapper>
						<Box display={"flex"} gridGap={15} onClick={() => navigateToStream(id, streamId)}>
							<img width={150} height={100} src={mainImageUrl} alt="stream thumbnail" />
							<Box display="flex" flexDirection="column">
								<StyledCellText style={{ width: "100%", marginBottom: "5px" }}>{name}</StyledCellText>
								<StyledCellText color={"textSecondary"} style={{ width: "100%" }}>
									{description}
								</StyledCellText>
							</Box>
						</Box>
					</StyledCellWrapper>
				),
				dataKey: "name_description"
			},
			{
				alignment: "center",
				label: <ColumnHeader>Date & Time</ColumnHeader>,
				Cell: ({ rowData: { startTime } }) => (
					<StyledCellWrapper center>
						<Box display="flex" flexDirection="column">
							<StyledCellText>{formatDate(new Date(startTime))}</StyledCellText>
							<StyledCellText>{formatTime(new Date(startTime))}</StyledCellText>
						</Box>
					</StyledCellWrapper>
				),
				dataKey: "date_time"
			},
			{
				alignment: "center",
				label: <ColumnHeader>Duration</ColumnHeader>,
				Cell: ({ rowData: { durationInSeconds } }) => (
					<StyledCellWrapper center>
						<Box display="flex" flexDirection="column">
							<StyledCellText>{convertDuration(durationInSeconds || 0)}</StyledCellText>
						</Box>
					</StyledCellWrapper>
				),
				dataKey: "duration"
			},
			{
				alignment: "center",
				label: <ColumnHeader>Max Viewers</ColumnHeader>,
				Cell: ({ rowData: { totalViewers } }) => (
					<StyledCellWrapper center>
						<Box display="flex" flexDirection="column">
							<StyledCellText>{totalViewers}</StyledCellText>
						</Box>
					</StyledCellWrapper>
				),
				dataKey: "duration"
			},
			{
				alignment: "center",
				label: <ColumnHeader>Audience</ColumnHeader>,
				Cell: ({ rowData: { contentAccessTypeId, allLocations, allowedLocations } }) => (
					<StyledCellWrapper center>
						<Box display="flex" flexDirection="column">
							<StyledCellText>{getAudience(contentAccessTypeId, allLocations, allowedLocations)}</StyledCellText>
						</Box>
					</StyledCellWrapper>
				),
				dataKey: "audience"
			},
			{
				alignment: "right",
				label: "",
				width: 100,
				Cell: ({ rowData }) => {
					return (
						<ActionMenu
							customMenuItems={[
								{
									label: "Edit",
									onClick: () => {
										history.push({
											pathname: `${routes.stream.createNew.getPath()}/${rowData.id}`,
											state: { stream: rowData }
										});
									},
									visible: true,
									disabled: false
								},
								{
									label: "Delete",
									onClick: () => setDeleteStream(rowData),
									visible: true,
									disabled: false
								},
								{
									label: rowData.isActive ? "Unpublish" : "Publish",
									onClick: () => {
										const { id, isActive, ...rest } = rowData;
										dispatch(
											updateLiveStream({
												id,
												data: { ...rest, isActive: !isActive },
												cb: () =>
													dispatch(
														fetchLiveStreamHistory({ include: ["allowedLocations"], orderBy: { createdAt: "DESC" } })
													)
											})
										);
									},
									visible: true,
									disabled: false
								}
							]}
						/>
					);
				}
			}
		],
		[dispatch, history, navigateToStream]
	);

	return (
		<WithTableContentLayout
			heading="Live Stream"
			isLoading={isFetchingHistory}
			actions={
				<CheckPermissions>
					<Button color={"primary"} variant={"filled"} onClick={() => history.push(routes.stream.createNew.getPath())}>
						Create Live Stream
					</Button>
				</CheckPermissions>
			}
			emptyState={<EmptyState description="No Streams found" />}
			onChangePage={page => {
				dispatch(fetchLiveStreamHistory({ page, perPage: HISTORY_PER_PAGE, include: ["allowedLocations"] }));
			}}
			totalItems={totalItems!}
			totalEntities={totalItems}
			tableTitle="Streams"
			perPage={perPage}
			page={page}
			tableColumns={tableColumns}
			data={items}
		>
			<DeleteModal
				open={!!deleteStream}
				title="Delete Stream"
				message="Are you sure you want to delete this stream?"
				cancelBtnText="No, Cancel"
				deleteBtnText="Yes, Delete"
				onClose={() => setDeleteStream(null)}
				onDelete={async () => {
					await dispatch(deleteLiveStream(deleteStream!.id));
					setDeleteStream(null);
				}}
			/>
		</WithTableContentLayout>
	);
};

export default StreamHistory;
